import {
  Box,
  FortoEmissionOffsetBadgeLightIcon,
  Header,
  HelpOutlineIcon,
  IconButton,
  IconButtonAnchor,
  makeStyles,
  MenuIcon,
  Popup,
  Theme,
  Tooltip,
} from '@freighthub/silhouette-core'
import cn from 'classnames'
import { CustomerFeature } from 'domain/Customer'
import { DefaultOffsetDialog } from 'features/BookingFlow'
import { LocalCurrencySelectorFlow } from 'features/CurrencyConversion'
import { NotificationWidget } from 'features/NotificationWidget'
import { FC, MouseEvent, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { authorize } from 'service/roles'
import { useAccess, useAccessToFeature } from 'service/useAccess'
import { useAuth } from 'service/useAuth'
import { useUIStore } from 'service/useUIStore'
import { UnleashFeatureFlagKeys, useUnleashFeatureFlags } from 'service/useUnleashFeatureFlags'
import { useI18n } from 'utils/localisation'

import { LOGO_URL_LIGHT_SVG } from '../../logoUrls'
import { getHoverColor } from './TopBarHoverStlyes'
import { TopBarIndicator } from './TopBarIndicator'
import { TopBarMenu } from './TopBarMenu'
import { TopBarSearch } from './TopBarSearch'

const testIds = {
  header: 'top-bar-root',
  navButton: 'top-bar-nav-button',
  customerLogin: 'top-bar-customer-login',
  demoMode: 'top-bar-demo-mode',
}

const useStyles = makeStyles<Theme>((theme) => ({
  button: {
    width: 32,
    height: 32,
    padding: 0,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: getHoverColor(theme),
    },
  },
  icon: {
    width: 20,
    height: 20,
  },
  logoWrapper: {
    flex: 'none',
    margin: theme.spacing(0, 1),
  },
  logo: {
    width: 80,
  },
  spacer: {
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      flex: 'none',
    },
  },
  hiddenOnMobile: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  tooltip: {
    fontSize: theme.typography.caption.fontSize,
  },
  sustainabilityIconRoot: {
    padding: 0,
  },
  sustainabilityIconImage: {
    display: 'flex',
    height: 28,
    width: 28,
  },
}))

export const TopBar: FC = (props) => {
  const {
    state: { loggedInAsCustomer, customer },
  } = useAuth()
  const { translate } = useI18n()
  const { toggleLeftPanel } = useUIStore()
  const classes = useStyles({})
  const isSustainabilityEnabled = useAccessToFeature(CustomerFeature.OFFSET_SERVICE)
  const { isEnabled } = useUnleashFeatureFlags()
  const showDefaultOffsetSetting = isSustainabilityEnabled && useAccess({ roles: authorize.admins })

  const defaultOffsetDialogTriggerRef = useRef<HTMLButtonElement>()

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const isPopupOpen = Boolean(anchorEl)

  const handleClickAway = () => {
    setAnchorEl(null)
  }
  const toggleDefaultOffsetDialog = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl((anchor) => {
      if (anchor) {
        return null
      }
      return event.currentTarget
    })
  }

  const closeDefaultOffsetDialog = () => {
    setAnchorEl(null)
  }

  const showLocalCurrencySelector = isEnabled(UnleashFeatureFlagKeys.LOCAL_CURRENCY_DISPLAY)

  return (
    <Header data-testid={testIds.header} {...props}>
      <IconButton
        onClick={toggleLeftPanel}
        className={classes.button}
        data-testid={testIds.navButton}
        data-userlane-id="button-toggle-sidebar"
      >
        <MenuIcon className={classes.icon} />
      </IconButton>
      <Link to="/" className={cn(classes.logoWrapper, classes.hiddenOnMobile)}>
        <img src={LOGO_URL_LIGHT_SVG} className={classes.logo} alt="forto-logo" />
      </Link>

      {showDefaultOffsetSetting && (
        <>
          <IconButton
            data-tracking-id="default-offset-dialog-logo-button"
            // @ts-expect-error TS(2322) FIXME: Type 'MutableRefObject<HTMLButtonElement | undefin... Remove this comment to see the full error message
            ref={defaultOffsetDialogTriggerRef}
            onClick={toggleDefaultOffsetDialog}
            classes={{ root: classes.sustainabilityIconRoot }}
          >
            <FortoEmissionOffsetBadgeLightIcon className={classes.sustainabilityIconImage} />
          </IconButton>
          <Popup
            width="fit-content"
            onClickAway={handleClickAway}
            anchorEl={anchorEl}
            open={isPopupOpen}
            placement="bottom-start"
            offset={{ top: 10 }}
            showArrow
          >
            <DefaultOffsetDialog onClose={closeDefaultOffsetDialog} />
          </Popup>
        </>
      )}

      <TopBarIndicator isDemo={customer.isDemo} loggedInAsCustomer={loggedInAsCustomer} />

      <div className={classes.spacer} />
      <TopBarSearch />
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="nowrap"
        data-userlane-id="header-icons-all"
      >
        {showLocalCurrencySelector && <LocalCurrencySelectorFlow />}
        <Tooltip
          title={translate('menu-help')}
          className={classes.hiddenOnMobile}
          classes={{ tooltip: classes.tooltip }}
        >
          <IconButtonAnchor href={translate('help_center-url')} target="_blank" className={classes.button}>
            <HelpOutlineIcon className={classes.icon} />
          </IconButtonAnchor>
        </Tooltip>
        <NotificationWidget />
        <TopBarMenu />
      </Box>
    </Header>
  )
}
